import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { 
    getAllPartnerPlaces,
    searchPartnerPlaces, 
    updatePartnerPlaceCache, 
    updatePartnerPlaceUntil, 
    updatePartnerPlaceUntilPlusMonth,
    updatePartnerPlace,
    deletePartnerPlace 
} from "../../api/AdminApi";
import { uploadImage } from "../../../mobile/api/ImageApi";

const PartnerPlaceManagementPage = () => {
    const [partnerPlaces, setPartnerPlaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 검색 관련 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('id');
    const [isSearchMode, setIsSearchMode] = useState(false);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPartnerPlace, setSelectedPartnerPlace] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    
    // 탭 관련 상태
    const [activeTab, setActiveTab] = useState('contract');
    
    // 계약사항 수정 폼
    const [formCache, setFormCache] = useState('');
    const [formUntil, setFormUntil] = useState('');
    
    // 정보 수정 폼
    const [formName, setFormName] = useState('');
    const [formAddress, setFormAddress] = useState('');
    const [formPhone, setFormPhone] = useState('');
    const [formLatitude, setFormLatitude] = useState('');
    const [formLongitude, setFormLongitude] = useState('');
    const [formPolicy, setFormPolicy] = useState('');
    const [formImageId, setFormImageId] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [imageUploading, setImageUploading] = useState(false);

    const fetchPartnerPlaces = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllPartnerPlaces(page, 20);
            setPartnerPlaces(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(false);
        } catch (error) {
            console.error('제휴매장 목록 조회 실패:', error);
            alert('제휴매장 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (page = 0) => {
        if (!searchQuery.trim()) {
            alert('검색어를 입력해주세요.');
            return;
        }
        
        setLoading(true);
        try {
            const response = await searchPartnerPlaces(page, 20, searchQuery, searchField);
            setPartnerPlaces(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(true);
        } catch (error) {
            console.error('제휴매장 검색 실패:', error);
            alert('제휴매장 검색에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (partnerPlace) => {
        setModalLoading(true);
        setModalOpen(true);
        setActiveTab('contract'); // 기본 탭으로 설정
        
        try {
            setSelectedPartnerPlace(partnerPlace);
            
            // 계약사항 수정 폼 초기화
            setFormCache(partnerPlace.cache || '');
            setFormUntil(partnerPlace.until ? new Date(partnerPlace.until).toISOString().slice(0, 16) : '');
            
            // 정보 수정 폼 초기화
            setFormName(partnerPlace.name || '');
            setFormAddress(partnerPlace.address || '');
            setFormPhone(partnerPlace.phone || '');
            setFormLatitude(partnerPlace.latitude || '');
            setFormLongitude(partnerPlace.longitude || '');
            setFormPolicy(partnerPlace.policy || '');
            setFormImageId(partnerPlace.imageId);
            setUploadedFileName('');
        } catch (error) {
            console.error('제휴매장 상세 조회 실패:', error);
            alert('제휴매장 정보를 불러오는데 실패했습니다.');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // 계약사항 수정 함수들
    const handleCacheUpdate = async () => {
        try {
            await updatePartnerPlaceCache(selectedPartnerPlace.id, parseInt(formCache));
            alert('입금액이 수정되었습니다.');
            fetchPartnerPlaces(currentPage);
            setSelectedPartnerPlace({...selectedPartnerPlace, cache: parseInt(formCache)});
        } catch (error) {
            console.error('입금액 수정 실패:', error);
            alert('입금액 수정에 실패했습니다.');
        }
    };

    const handleUntilUpdate = async () => {
        try {
            await updatePartnerPlaceUntil(selectedPartnerPlace.id, formUntil);
            alert('제휴기간이 수정되었습니다.');
            fetchPartnerPlaces(currentPage);
            setSelectedPartnerPlace({...selectedPartnerPlace, until: formUntil});
        } catch (error) {
            console.error('제휴기간 수정 실패:', error);
            alert('제휴기간 수정에 실패했습니다.');
        }
    };

    const handleUntilPlusMonth = async () => {
        const confirmed = window.confirm('정말로 제휴기간을 1달 추가하시겠습니까?');
        if (!confirmed) return;
        
        try {
            await updatePartnerPlaceUntilPlusMonth(selectedPartnerPlace.id);
            alert('제휴기간이 1달 추가되었습니다.');
            
            // 목록과 모달 새로고침
            await fetchPartnerPlaces(currentPage);
            
            // 새로고침된 목록에서 현재 선택된 제휴매장 찾아서 모달 업데이트
            const response = await getAllPartnerPlaces(currentPage, 20);
            const updatedPartnerPlace = response.content.find(p => p.id === selectedPartnerPlace.id);
            if (updatedPartnerPlace) {
                setSelectedPartnerPlace(updatedPartnerPlace);
                setFormUntil(updatedPartnerPlace.until ? new Date(updatedPartnerPlace.until).toISOString().slice(0, 16) : '');
            }
        } catch (error) {
            console.error('제휴기간 1달 추가 실패:', error);
            alert('제휴기간 1달 추가에 실패했습니다.');
        }
    };

    const handleDeletePartnerPlace = async () => {
        const confirmed = window.confirm(`정말로 "${selectedPartnerPlace.name}" 제휴매장을 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;
        
        try {
            await deletePartnerPlace(selectedPartnerPlace.id);
            alert('제휴매장이 삭제되었습니다.');
            setModalOpen(false);
            // 목록 새로고침
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(currentPage);
            } else {
                fetchPartnerPlaces(currentPage);
            }
        } catch (error) {
            console.error('제휴매장 삭제 실패:', error);
            alert('제휴매장 삭제에 실패했습니다.');
        }
    };

    // 정보 수정 함수들
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setImageUploading(true);
        try {
            const response = await uploadImage(file);
            setFormImageId(response.id);
            setUploadedFileName(file.name);
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다.');
        } finally {
            setImageUploading(false);
        }
    };

    const handleImageRemove = () => {
        setFormImageId(null);
        setUploadedFileName('');
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleInfoUpdate = async () => {
        if (!formName.trim() || !formAddress.trim() || !formPhone.trim() || !formPolicy.trim()) {
            alert('모든 필수 필드를 입력해주세요.');
            return;
        }

        const updateData = {
            name: formName,
            address: formAddress,
            phone: formPhone,
            latitude: parseFloat(formLatitude) || null,
            longitude: parseFloat(formLongitude) || null,
            policy: formPolicy,
            imageId: formImageId
        };

        try {
            await updatePartnerPlace(selectedPartnerPlace.id, updateData);
            alert('제휴매장 정보가 수정되었습니다.');
            fetchPartnerPlaces(currentPage);
        } catch (error) {
            console.error('제휴매장 정보 수정 실패:', error);
            alert('제휴매장 정보 수정에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchPartnerPlaces();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(page);
            } else {
                fetchPartnerPlaces(page);
            }
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        handleSearch(0);
    };

    const handleReset = () => {
        setSearchQuery('');
        setCurrentPage(0);
        fetchPartnerPlaces(0);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    // 테이블 컬럼 정의 (PartnerPlaceResponse 기준)
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'memberId', field: 'memberId', minWidth: 'min-w-16' },
        { header: 'name', field: 'name', minWidth: 'min-w-32' },
        { 
            header: 'address', 
            field: 'address', 
            minWidth: 'min-w-48',
            render: (place) => {
                if (!place.address) return '';
                return place.address.length > 30 ? `${place.address.substring(0, 30)}...` : place.address;
            }
        },
        { header: 'phone', field: 'phone', minWidth: 'min-w-24' },
        { 
            header: 'latitude', 
            field: 'latitude', 
            minWidth: 'min-w-20',
            render: (place) => place.latitude || ''
        },
        { 
            header: 'longitude', 
            field: 'longitude', 
            minWidth: 'min-w-20',
            render: (place) => place.longitude || ''
        },
        { 
            header: 'policy', 
            field: 'policy', 
            minWidth: 'min-w-32',
            render: (place) => {
                if (!place.policy) return '';
                return place.policy.length > 20 ? `${place.policy.substring(0, 20)}...` : place.policy;
            }
        },
        { 
            header: 'imageId', 
            field: 'imageId', 
            minWidth: 'min-w-16',
            render: (place) => place.imageId || ''
        },
        { 
            header: 'until', 
            field: 'until', 
            minWidth: 'min-w-32',
            render: (place) => formatDateTime(place.until)
        },
        { 
            header: 'cache', 
            field: 'cache', 
            minWidth: 'min-w-16',
            render: (place) => place.cache || 0
        },
        { 
            header: 'createdAt', 
            field: 'createdAt', 
            minWidth: 'min-w-32',
            render: (place) => formatDateTime(place.createdAt)
        },
        { 
            header: 'updatedAt', 
            field: 'updatedAt', 
            minWidth: 'min-w-32',
            render: (place) => formatDateTime(place.updatedAt)
        },
        { 
            header: 'count', 
            field: 'count', 
            minWidth: 'min-w-16',
            render: (place) => place.count || 0
        },
        { header: 'memberPhone', field: 'memberPhone', minWidth: 'min-w-24' }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">제휴매장 관리</h1>

                {/* 검색 폼 */}
                <div className="mb-3 p-2 bg-gray-100 border border-gray-300">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 text-xs">
                        <div className="flex gap-2">
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="id" 
                                    checked={searchField === 'id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                ID
                            </label>
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="member_id" 
                                    checked={searchField === 'member_id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                회원ID
                            </label>
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="name" 
                                    checked={searchField === 'name'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                매장명
                            </label>
                        </div>
                        <input 
                            type="text" 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="검색어 입력"
                            className="px-2 py-1 border border-gray-300 text-xs"
                        />
                        <button type="submit" className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            검색
                        </button>
                        <button type="button" onClick={handleReset} className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            전체보기
                        </button>
                    </form>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}개 | {currentPage + 1}/{totalPages} 페이지
                    {isSearchMode && ` | 검색: ${searchField}="${searchQuery}"`}
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={partnerPlaces}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 제휴매장 상세 모달 */}
                <InfoModal 
                    isOpen={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    title={`제휴매장 정보 - ${selectedPartnerPlace?.name || ''}`}
                    width="w-[700px]"
                >
                    {modalLoading ? (
                        <div className="text-center py-4">제휴매장 정보를 불러오는 중...</div>
                    ) : selectedPartnerPlace ? (
                        <div className="text-xs">
                            {/* 탭 */}
                            <div className="mb-4">
                                <div className="flex border-b border-gray-400">
                                    <button
                                        onClick={() => handleTabChange('contract')}
                                        className={`px-3 py-2 text-xs border-l border-t border-r border-gray-400 ${
                                            activeTab === 'contract' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        계약사항 수정
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('info')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'info' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        정보 수정
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('image')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'image' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        이미지 보기
                                    </button>
                                </div>
                            </div>

                            {/* 탭 내용 */}
                            {activeTab === 'contract' ? (
                                /* 계약사항 수정 탭 */
                                <div className="space-y-4">
                                    {/* 기본 정보 표시 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">제휴매장 기본 정보</h3>
                                        <table className="w-full border border-gray-400">
                                            <tbody>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedPartnerPlace.id}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">name</td><td className="border border-gray-400 px-2 py-1">{selectedPartnerPlace.name}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">memberPhone</td><td className="border border-gray-400 px-2 py-1">{selectedPartnerPlace.memberPhone}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedPartnerPlace.createdAt)}</td></tr>
                                                <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">updatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedPartnerPlace.updatedAt)}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* 입금액 수정 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">입금액 수정</h3>
                                        <div className="flex gap-2 items-center">
                                            <span className="text-xs">현재 입금액: {selectedPartnerPlace.cache || 0}원</span>
                                        </div>
                                        <div className="flex gap-2 items-center mt-2">
                                            <input
                                                type="number"
                                                value={formCache}
                                                onChange={(e) => setFormCache(e.target.value)}
                                                className="px-2 py-1 border border-gray-300 flex-1"
                                                placeholder="새로운 입금액 입력"
                                            />
                                            <button
                                                onClick={handleCacheUpdate}
                                                className="px-3 py-1 bg-blue-200 border border-gray-400 hover:bg-blue-300"
                                            >
                                                저장
                                            </button>
                                        </div>
                                    </div>

                                    {/* 제휴기간 수정 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">제휴기간 수정</h3>
                                        <div className="flex gap-2 items-center">
                                            <span className="text-xs">현재 제휴 만료 기간: {formatDateTime(selectedPartnerPlace.until)}</span>
                                        </div>
                                        <div className="flex gap-2 items-center mt-2">
                                            <input
                                                type="datetime-local"
                                                value={formUntil}
                                                onChange={(e) => setFormUntil(e.target.value)}
                                                className="px-2 py-1 border border-gray-300 flex-1"
                                            />
                                            <button
                                                onClick={handleUntilUpdate}
                                                className="px-3 py-1 bg-blue-200 border border-gray-400 hover:bg-blue-300"
                                            >
                                                저장
                                            </button>
                                        </div>
                                    </div>

                                    {/* 제휴기간 1달 추가 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">제휴기간 1달 추가</h3>
                                        <button
                                            onClick={handleUntilPlusMonth}
                                            className="px-3 py-1 bg-green-200 border border-gray-400 hover:bg-green-300 text-green-800"
                                        >
                                            제휴기간 1달 추가
                                        </button>
                                    </div>

                                    {/* 제휴매장 삭제 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">제휴매장 삭제</h3>
                                        <button
                                            onClick={handleDeletePartnerPlace}
                                            className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                        >
                                            제휴매장 삭제
                                        </button>
                                    </div>
                                </div>
                            ) : activeTab === 'info' ? (
                                /* 정보 수정 탭 */
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">제휴매장 정보 수정</h3>
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">매장 이름</label>
                                                <input 
                                                    type="text" 
                                                    value={formName}
                                                    onChange={(e) => setFormName(e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                    placeholder="매장 이름을 입력하세요"
                                                    maxLength="20"
                                                />
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">주소</label>
                                                <input 
                                                    type="text" 
                                                    value={formAddress}
                                                    onChange={(e) => setFormAddress(e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                    placeholder="매장 주소를 입력하세요"
                                                    maxLength="100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">전화번호</label>
                                                <input 
                                                    type="text" 
                                                    value={formPhone}
                                                    onChange={(e) => setFormPhone(e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                    placeholder="매장 전화번호를 입력하세요"
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="block mb-1 text-xs font-medium">위도</label>
                                                    <input 
                                                        type="number" 
                                                        step="any"
                                                        value={formLatitude}
                                                        onChange={(e) => setFormLatitude(e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                        placeholder="위도"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="block mb-1 text-xs font-medium">경도</label>
                                                    <input 
                                                        type="number" 
                                                        step="any"
                                                        value={formLongitude}
                                                        onChange={(e) => setFormLongitude(e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                        placeholder="경도"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">제휴 혜택</label>
                                                <textarea 
                                                    value={formPolicy}
                                                    onChange={(e) => setFormPolicy(e.target.value)}
                                                    rows="5"
                                                    className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                    placeholder="제휴 혜택을 입력하세요"
                                                    maxLength="300"
                                                />
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">이미지</label>
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <input 
                                                            type="file" 
                                                            accept="image/*"
                                                            onChange={handleImageUpload}
                                                            className="text-xs"
                                                            disabled={imageUploading}
                                                        />
                                                        {imageUploading && <span className="text-xs text-gray-500">업로드 중...</span>}
                                                    </div>
                                                    {formImageId && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-green-600">
                                                                {uploadedFileName ? '이미지 업로드됨' : '기존 이미지'}
                                                            </span>
                                                            <button
                                                                onClick={handleImageRemove}
                                                                className="px-2 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800 text-xs"
                                                            >
                                                                이미지 삭제
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={handleInfoUpdate}
                                                    className="px-3 py-1 bg-blue-200 border border-gray-400 hover:bg-blue-300"
                                                >
                                                    정보 수정
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : activeTab === 'image' ? (
                                /* 이미지 보기 탭 */
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">제휴매장 이미지</h3>
                                        {selectedPartnerPlace.imageUrl ? (
                                            <div className="border border-gray-300 p-4 text-center">
                                                <img 
                                                    src={selectedPartnerPlace.imageUrl} 
                                                    alt="제휴매장 이미지" 
                                                    className="max-w-full max-h-96 mx-auto border border-gray-300"
                                                />
                                            </div>
                                        ) : (
                                            <div className="border border-gray-300 p-8 text-center text-gray-500">
                                                이미지가 없습니다.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default PartnerPlaceManagementPage;